import { useQuery } from '@tanstack/react-query'
import { simulateSwapOp, UserOps } from '@towns/userops'
import axios, { AxiosError } from 'axios'
import { SECOND_MS } from 'data/constants'
import { parseInt } from 'lodash-es'
import {
    calculateMinAmountBase,
    getETHInputAmountAfterFees,
    NULL_ADDRESS,
    SpaceAddressFromSpaceId,
    useBaseWrites,
} from 'use-towns-client'
import { env } from 'utils'
import type { Address, Hex } from 'viem'
import { createPublicClient, http, isAddress } from 'viem'
import { base } from 'viem/chains'
import { z } from 'zod'

import { ETH_ADDRESS } from '../../utils'

export type LifiQuote = {
    id: string
    estimate: {
        approvalAddress: string
        fromAmount: string
        fromAmountUSD: string
        toAmount: string
        toAmountMin: string
        toAmountUSD: string
    }
    type: string
    transactionRequest: {
        chainId?: number | null
        data: string
        from?: string | null
        gasLimit?: string | null
        gasPrice?: string | null
        to?: string | null
        value?: string | null
    }
}

const zLifiQuote: z.ZodType<LifiQuote> = z.object({
    id: z.string(),
    estimate: z.object({
        approvalAddress: z.string(),
        fromAmount: z.string(),
        fromAmountUSD: z.string(),
        toAmount: z.string(),
        toAmountMin: z.string(),
        toAmountUSD: z.string(),
    }),
    type: z.string(),
    transactionRequest: z.object({
        chainId: z.number().nullish(),
        data: z.string(),
        from: z.string().nullish(),
        gasLimit: z.string().nullish(),
        gasPrice: z.string().nullish(),
        to: z.string().nullish(),
        value: z.string().nullish(),
    }),
})

export type QuoteProps = {
    fromChain: string
    toChain: string
    fromToken: string
    toToken: string
    fromAmount: string
    fromAddress: string
    currentTokenBalance: bigint
    slippage: number
    walletAddress: string
    spaceId: string | undefined
    swapRouterAddress: Address | undefined
}

export const useLifiQuote = (props: QuoteProps) => {
    const {
        fromChain,
        toChain,
        fromToken,
        toToken,
        fromAmount,
        fromAddress,
        currentTokenBalance,
        slippage,
    } = props

    const { userOpsInstance } = useBaseWrites()

    const enabled =
        fromChain.length > 0 &&
        toChain.length > 0 &&
        fromToken.length > 0 &&
        toToken.length > 0 &&
        fromAddress.length > 0 &&
        fromAmount.length > 0 &&
        fromToken.length > 0 &&
        parseInt(fromAmount) > 0 &&
        currentTokenBalance >= BigInt(fromAmount)

    const queryKey = [
        'lifiQuote',
        fromChain,
        toChain,
        fromToken,
        toToken,
        fromAmount,
        fromAddress,
        slippage,
        props.spaceId,
        props.swapRouterAddress,
        props.walletAddress,
    ]
    const { data, error, isError, isLoading } = useQuery({
        queryKey,
        enabled: enabled,
        queryFn: async () => {
            try {
                if (props.fromChain === 'solana-mainnet') {
                    return getSolanaQuote(props)
                } else {
                    return getBaseQuote(props, userOpsInstance)
                }
            } catch (e) {
                console.error(e) // Keep full error logging for debugging
                if (e instanceof AxiosError) {
                    if (e.response?.status === 404) {
                        throw new Error('No route found')
                    }

                    switch (e.response?.data?.code) {
                        case 1003:
                            throw new Error('Token not found')
                        default:
                            console.error('LI.FI API error details:', e.response?.data?.message)
                            throw new Error(e.response?.data?.message ?? 'Something went wrong')
                    }
                }
                throw new Error('Something went wrong')
            }
        },
        staleTime: SECOND_MS * 30,
        retry: false,
    })

    return { data, isLoading, isError, error }
}

async function getSolanaQuote(props: QuoteProps) {
    // `fee` = The percent of the integrator's fee that is taken from every transaction. 0.02 represents 2%. The maximum fee amount is 10%.
    const fee =
        props.toChain === 'solana-mainnet' && env.VITE_LIFI_INTEGRATOR_ID
            ? env.VITE_LIFI_FEE
            : undefined

    const result = await axios.get('https://li.quest/v1/quote', {
        params: {
            fromChain: '1151111081099710',
            toChain: '1151111081099710',
            fromToken: props.fromToken,
            toToken: props.toToken,
            fromAmount: props.fromAmount,
            fromAddress: props.fromAddress,
            slippage: props.slippage,
            integrator: env.VITE_LIFI_INTEGRATOR_ID,
            fee: fee,
        },
        headers: {
            'x-lifi-api-key': env.VITE_LIFI_API_KEY,
        },
    })
    if (!result.data) {
        throw new Error('No data')
    }
    return zLifiQuote.parse(result.data)
}

async function getLifiBaseQuote(params: QuoteProps) {
    if (params.toChain === 'solana-mainnet') {
        throw new Error('Solana is not supported')
    }

    const result = await axios.get('https://li.quest/v1/quote', {
        params: {
            fromChain: params.fromChain,
            toChain: params.toChain,
            fromToken: params.fromToken,
            toToken: params.toToken,
            fromAmount: params.fromAmount,
            fromAddress: params.fromAddress,
            slippage: 0.15, // arbitrary, high amount. actual slippage check happens in the swap router
            denyExchanges: ['paraswap'], // ParaSwap cannot handle fee-on-transfer tokens
        },
        headers: {
            'x-lifi-api-key': env.VITE_LIFI_API_KEY,
        },
    })
    if (!result.data) {
        throw new Error('No data')
    }
    return zLifiQuote.parse(result.data)
}

async function getBaseQuote(params: QuoteProps, userOpsInstance: UserOps | undefined) {
    if (!params.swapRouterAddress) {
        throw new Error('Swap router address is not set')
    }

    // For space trades: SwapFacet calls SwapRouter, so caller and poster are both the space address
    // For DM trades: User calls SwapRouter directly, so caller is user and poster is NULL
    const caller = (
        params.spaceId ? SpaceAddressFromSpaceId(params.spaceId) : params.walletAddress
    ) as Address
    const poster = (
        params.spaceId ? SpaceAddressFromSpaceId(params.spaceId) : NULL_ADDRESS
    ) as Address

    let fromAmountAfterFees = params.fromAmount

    const publicClient = createPublicClient({
        chain: base,
        transport: http(env.VITE_BASE_CHAIN_RPC_URL ?? ''),
    })

    if (params.fromToken === NULL_ADDRESS) {
        const { amountInAfterFees } = await getETHInputAmountAfterFees({
            amountIn: params.fromAmount,
            caller,
            poster,
            swapRouterAddress: params.swapRouterAddress,
            publicClient,
        })
        fromAmountAfterFees = amountInAfterFees
    }

    const quote = await getLifiBaseQuote({ ...params, fromAmount: fromAmountAfterFees })
    quote.transactionRequest.value = params.fromToken === NULL_ADDRESS ? params.fromAmount : '0x0'
    const remappedTokenInAddress =
        params.fromToken === NULL_ADDRESS ? ETH_ADDRESS : params.fromToken
    const remappedTokenOutAddress = params.toToken === NULL_ADDRESS ? ETH_ADDRESS : params.toToken

    const routerAddress = quote.transactionRequest.to ?? ''
    if (!isAddress(routerAddress)) {
        throw new Error(`Invalid router address: ${routerAddress}`)
    }

    const baseCalculateParams = {
        tokenIn: remappedTokenInAddress as Address,
        tokenOut: remappedTokenOutAddress as Address,
        amountIn: params.fromAmount,
        walletAddress: params.walletAddress as Address,
        approveTarget: quote.estimate.approvalAddress as Address,
        router: routerAddress as Address,
        swapData: quote.transactionRequest.data as Hex,
        slippage: params.slippage,
        value: quote.transactionRequest.value ?? '0x0',
        baseRpcUrl: env.VITE_BASE_CHAIN_RPC_URL ?? '',
        poster,
        simulateSwapOutput: async (simulateParams: Parameters<typeof simulateSwapOp>[1]) => {
            if (!userOpsInstance) {
                return Promise.reject(new Error('UserOps instance is not initialized'))
            }

            const result = await simulateSwapOp(
                publicClient as Parameters<typeof simulateSwapOp>[0],
                simulateParams,
            )
            if (!result) {
                throw new Error('Unable to simulate transaction')
            }
            return result
        },
    }

    const swapContext =
        params.spaceId !== undefined
            ? { spaceId: params.spaceId, swapRouterAddress: undefined }
            : { spaceId: undefined, swapRouterAddress: params.swapRouterAddress }

    const { toAmount, toAmountMin } = await calculateMinAmountBase({
        ...baseCalculateParams,
        ...swapContext,
    })
    quote.estimate.toAmount = toAmount
    quote.estimate.toAmountMin = toAmountMin
    quote.estimate.fromAmount = params.fromAmount
    return quote
}
